import {
  Component,
  Input,
  OnInit,
  ChangeDetectorRef,
  AfterViewInit,
  NgZone,
  ElementRef,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';

import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
  heroChevronLeftSolid,
  heroChevronRightSolid,
} from '@ng-icons/heroicons/solid';
import { bootstrapCaretDownFill } from '@ng-icons/bootstrap-icons';

import { DAYS, MONTHS } from './calendar-day';

import { CalendarDay } from './calendar-day';
import {
  featherChevronDown,
  featherChevronLeft,
  featherChevronRight,
} from '@ng-icons/feather-icons';
import { OverlayModule } from '@angular/cdk/overlay';

@Component({
  selector: 'ui-calendar',
  imports: [CommonModule, NgIconComponent, OverlayModule],
  templateUrl: './calendar.component.html',
  viewProviders: [
    provideIcons({
      bootstrapCaretDownFill,
      heroChevronLeftSolid,
      heroChevronRightSolid,
      featherChevronLeft,
      featherChevronRight,
      featherChevronDown,
    }),
  ],
})
export class CalendarComponent implements OnInit, AfterViewInit {
  private cdr = inject(ChangeDetectorRef);
  private ngZone = inject(NgZone);

  @Input() selected?: Date = new Date();
  readonly changed = output<Date>();
  readonly period = output<{
    startDate: Date;
    endDate: Date;
  }>();
  readonly bookingType = input<'date' | 'time'>();
  readonly dayToDisable = input<Date[]>();
  readonly isOpenSpace = input<boolean>();

  readonly changeMonth = output<{
    direction: string;
  }>();
  @Input() set updateDirection(value: { direction: string }) {
    if (value.direction === 'next') this.next();
    else this.previous();
  }

  readonly triggerYear =
    viewChild.required<ElementRef<HTMLButtonElement>>('yearRef');
  readonly triggerMonth =
    viewChild.required<ElementRef<HTMLButtonElement>>('monthRef');

  opened = false;
  openedMonth = false;

  mode: 'days' | 'years' = 'days';
  private _direction: 'next' | 'previous' | null;
  days = DAYS;
  months = MONTHS;
  month = 0;
  year = 0;
  years: number[] = [];
  monthDays: number[] = [];
  weeks = [0, 1, 2, 3, 4, 5];
  calendarDays: Map<number, CalendarDay[]> = new Map();
  today = new Date();
  hasBottomSpace = false;
  startDate: Date | undefined;
  endDate: Date | undefined;

  ngOnInit(): void {
    this.initialize();
    this.updateCalendar();
  }

  ngAfterViewInit() {
    this.cdr.detectChanges(); // Manually trigger change detection
  }

  private initialize(): void {
    const selected = this.selected || this.today;
    this.month = selected.getMonth();
    this.year = selected.getFullYear();
  }

  openYear(): void {
    const rect = this.triggerYear().nativeElement.getBoundingClientRect();
    const bottomSpace = window.innerHeight - rect.bottom;
    this.hasBottomSpace = bottomSpace > 300;
    this.opened = true;
    this.updateYears();
  }

  openMonth(): void {
    const rect = this.triggerMonth().nativeElement.getBoundingClientRect();
    const bottomSpace = window.innerHeight - rect.bottom;
    this.hasBottomSpace = bottomSpace > 300;
    this.openedMonth = true;
  }

  close(): void {
    this.opened = false;
    this.openedMonth = false;
  }

  selectDay(day: CalendarDay): void {
    this.selected = new Date(day.year, day.month, day.day);
    this.changed.emit(this.selected);
    if (
      this.selected.getDate() === this.startDate?.getDate() &&
      this.selected.getMonth() === this.startDate?.getMonth() &&
      this.selected.getFullYear() === this.startDate?.getFullYear()
    ) {
      this.startDate = undefined;
      this.endDate = undefined;
    } else {
      if (this.bookingType() === 'time') this.startDate = this.selected;
      else if (this.startDate)
        this.endDate = new Date(day.year, day.month, day.day);
      else this.startDate = new Date(day.year, day.month, day.day);
    }
  }

  changeCalendar(direction: 'next' | 'previous'): void {
    const increment = direction === 'next' ? 1 : -1;

    if (this.month === (direction === 'next' ? 11 : 0)) {
      this.year += increment;
      this.month = direction === 'next' ? 0 : 11;
    } else {
      this.month += increment;
    }

    this.updateCalendar();
  }

  next(): void {
    this.changeCalendar('next');
    this.changeMonth.emit({ direction: 'next' });
  }

  previous(): void {
    this.changeCalendar('previous');
    this.changeMonth.emit({ direction: 'previous' });
  }

  toggleYears(): void {
    if (this.mode === 'days') {
      this.mode = 'years';
      this.updateYears();
    } else {
      this.mode = 'days';
    }
  }

  selectYear(year: number): void {
    this.year = year;
    this.updateCalendar();
    this.close();
  }

  selectMonth(month: number): void {
    this.month = month;
    this.updateCalendar();
    this.close();
  }

  private updateYears(): void {
    this.years = Array.from({ length: 24 }, (_, i) => this.year - 12 + i);
  }

  private getDay(date: Date): number {
    const day = date.getDay();

    return day === 0 ? 6 : day - 1;
  }

  private getMonthDetails(): any {
    const daysInMonth = new Date(this.year, this.month + 1, 0).getDate();
    const firstDay = this.getDay(new Date(this.year, this.month, 1));
    const lastDay = this.getDay(new Date(this.year, this.month, daysInMonth));
    const previousMonth = this.month === 0 ? 11 : this.month - 1;
    const nextMonth = this.month === 11 ? 0 : this.month + 1;
    const previousMonthYear = this.month === 0 ? this.year - 1 : this.year;
    const nextMonthYear = this.month === 11 ? this.year + 1 : this.year;
    const previousMonthDaysInMonth = new Date(
      previousMonthYear,
      previousMonth + 1,
      0
    ).getDate();

    return {
      daysInMonth,
      firstDay,
      lastDay,
      previousMonth,
      nextMonth,
      previousMonthYear,
      nextMonthYear,
      previousMonthDaysInMonth,
    };
  }

  private populateDays(
    start: number,
    end: number,
    month: number,
    year: number,
    count: number
  ): number {
    for (let i = start; i <= end; i++) {
      const week = Math.floor(count / 7);
      if (!this.calendarDays.has(week)) {
        this.calendarDays.set(week, []);
      }

      this.calendarDays.get(week)?.push({
        day: i,
        month: month,
        year: year,
      });

      count += 1;
    }

    return count;
  }

  private updateCalendar(): void {
    const monthDetails = this.getMonthDetails();
    let count = 0;

    this.calendarDays.clear();
    this.monthDays = [];

    count = this.populateDays(
      monthDetails.previousMonthDaysInMonth - monthDetails.firstDay + 1,
      monthDetails.previousMonthDaysInMonth,
      monthDetails.previousMonth,
      monthDetails.previousMonthYear,
      count
    );

    count = this.populateDays(
      1,
      monthDetails.daysInMonth,
      this.month,
      this.year,
      count
    );

    this.populateDays(
      1,
      6 - monthDetails.lastDay,
      monthDetails.nextMonth,
      monthDetails.nextMonthYear,
      count
    );
  }

  activeDate(day: CalendarDay): boolean | undefined {
    return (
      (this.selected?.getDate() == day.day &&
        this.selected?.getMonth() == day.month &&
        this.selected.getFullYear() == day.year) ||
      (this.startDate &&
        day.day === this.startDate.getDate() &&
        day.month === this.startDate.getMonth() &&
        day.year === this.startDate.getFullYear()) ||
      (this.endDate &&
        day.day === this.endDate.getDate() &&
        day.month === this.endDate.getMonth() &&
        day.year === this.endDate.getFullYear())
    );
  }

  disabledUsedDate(date: Date): boolean {
    const dayToDisable = this.dayToDisable();
    if (!dayToDisable) return false;
    for (const day of dayToDisable) {
      if (
        day.getDate() === date.getDate() &&
        day.getMonth() === date.getMonth() &&
        day.getFullYear() === date.getFullYear()
      ) {
        return true;
      }
    }
    return false;
  }

  disabledButton(day: CalendarDay): boolean | undefined {
    const dayDate = this.generateDate(day);
    if (this.isOpenSpace())
      return (
        dayDate.getDay() === 0 ||
        ((dayDate.getDate() < this.today.getDate() ||
          (this.today.getDate() == dayDate.getDate() &&
            this.today.getHours() >= 18)) &&
          dayDate.getMonth() === this.today.getMonth() &&
          dayDate.getFullYear() === this.today.getFullYear()) ||
        (dayDate.getMonth() < this.today.getMonth() &&
          dayDate.getFullYear() == this.today.getFullYear()) ||
        dayDate.getFullYear() < this.today.getFullYear() ||
        (this.startDate &&
          this.generateDate(day) < this.startDate &&
          this.bookingType() !== 'time')
      );

    return (
      this.disabledUsedDate(dayDate) ||
      dayDate.getDay() === 0 ||
      ((dayDate.getDate() < this.today.getDate() ||
        (this.today.getDate() == dayDate.getDate() &&
          this.today.getHours() >= 18)) &&
        dayDate.getMonth() === this.today.getMonth() &&
        dayDate.getFullYear() === this.today.getFullYear()) ||
      (dayDate.getMonth() < this.today.getMonth() &&
        dayDate.getFullYear() == this.today.getFullYear()) ||
      dayDate.getFullYear() < this.today.getFullYear() ||
      (this.startDate &&
        this.generateDate(day) < this.startDate &&
        this.bookingType() !== 'time')
    );
  }

  generateDate(day: CalendarDay): Date {
    const date = new Date(day.year, day.month, day.day);
    date.setHours(0, 0);
    return date;
  }

  showPeriod(day: CalendarDay): boolean | undefined {
    return (
      this.bookingType() !== 'time' &&
      this.startDate &&
      this.endDate &&
      day.month === this.month &&
      this.generateDate(day) > this.startDate &&
      this.generateDate(day) < this.endDate
    );
  }
}
